// The in-app viewer for a search result. On phones the old top bar sat at the
// very top of the screen, and with viewport-fit=cover that is under the status
// bar / notch, so "Back" could not be seen or tapped. Now:
//   - the bar is padded by the safe-area inset, sticky, and above the site
//     header; "← Back to chat" (left) and "Open in new tab ↗" (right) are 44px
//     tap targets that stay put while the page in the frame scrolls;
//   - opening the panel pushes a history entry, so the browser / phone back
//     button closes it and returns to the chat instead of leaving the site.
import React, { useEffect, useRef } from "react";

const PANEL_STATE = "chatcamilleLinkPanel";

export default function LinkPanel({ open, url, title, onClose }) {
  const pushed = useRef(false);

  // One history entry per opening; back (button or gesture) closes the panel.
  useEffect(() => {
    if (!open) return undefined;
    try {
      window.history.pushState({ ...(window.history.state || {}), [PANEL_STATE]: true }, "");
      pushed.current = true;
    } catch (_) {
      pushed.current = false;
    }
    const onPop = () => { pushed.current = false; onClose(); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, onClose]);

  if (!open) return null;

  // Our own Back: undo the entry we pushed (popstate then closes the panel).
  const back = () => {
    if (pushed.current && window.history.state && window.history.state[PANEL_STATE]) window.history.back();
    else onClose();
  };

  const tap = { minHeight: "44px", minWidth: "44px", boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "22px", fontSize: "14px", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, textDecoration: "none", cursor: "pointer" };

  return (
    <div role="dialog" aria-modal="true" aria-label={title || "Search result"} data-testid="link-panel"
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, height: "100dvh", zIndex: 9600, background: "rgba(0,0,0,0.95)", display: "flex", flexDirection: "column" }}>
      <div data-testid="link-panel-bar"
        style={{ position: "sticky", top: 0, zIndex: 1, flexShrink: 0, display: "flex", alignItems: "center", gap: "8px",
          padding: "6px 10px", paddingTop: "max(6px, env(safe-area-inset-top, 0px))",
          paddingLeft: "max(10px, env(safe-area-inset-left, 0px))", paddingRight: "max(10px, env(safe-area-inset-right, 0px))",
          background: "rgba(28,18,52,0.98)", borderBottom: "1px solid rgba(107,78,170,0.45)" }}>
        <button type="button" onClick={back} aria-label="Back to chat"
          style={{ ...tap, padding: "0 14px", border: "1.5px solid rgba(155,127,212,0.7)", background: "rgba(107,78,170,0.45)", color: "#F0E6FF" }}>
          ← Back to chat
        </button>
        <span style={{ flex: 1, minWidth: 0, fontSize: "11px", color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title || url}
        </span>
        <a href={url} target="_blank" rel="noopener noreferrer" aria-label="Open in new tab"
          style={{ ...tap, padding: "0 12px", border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.08)", color: "#D4ADFF" }}>
          Open in new tab ↗
        </a>
      </div>
      <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
        <iframe src={url} title={title || "Search Result"} style={{ display: "block", border: "none", width: "100%", height: "100%", background: "#fff" }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox" />
      </div>
      <div style={{ padding: "6px 12px", paddingBottom: "max(6px, env(safe-area-inset-bottom, 0px))", fontSize: "11px", color: "rgba(255,255,255,0.45)", textAlign: "center", flexShrink: 0 }}>
        Preview shown on chatcamille — some sites may not allow in-app viewing.
      </div>
    </div>
  );
}
